/* eslint-disable camelcase */
import React, { Component, createElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { loadImage } from 'bianco.images-loader';
import { debounce, get } from 'lodash';
import reduxUtils from 'util/redux';
import { connect } from 'react-redux';
import tracking from 'tracking';
import stopPropagation from 'util/dom/stop-propagation';
import normaliseTrailingSlash from 'util/misc/normalise-trailing-slash';

import { PRODUCT_LIST_CATEGORY } from 'tracking/constants';
import { BADGE_TILE, THUMB, TILE } from 'constants/api-images-types';
import { imageByDpi, productImage } from 'util/react/stack-images-path';

import { COUNTER_CLICK_DEBOUNCE } from 'constants/debounce-delays';
import { OPEN } from 'constants/order-states';
import t from 'translations';
import Icon from 'components/icon';
import Loader from 'components/loader';
import CartCounter from 'components/cart-counter';
import ProductMeta from 'components/product-meta';
import categoryIcons from 'constants/category-icons';
import mapFacets from 'util/react/facet';
import { register, unregister } from 'util/dom/channeled-interactionobserver';

const DEFAULT_NAME = 'Loading...';

const assetsCache = new Map();

class ProductTile extends Component {
    static defaultProps = {
        name: DEFAULT_NAME,
        slug: 'undefined',
        list: PRODUCT_LIST_CATEGORY,
        skipTests: false,
        has_discount: false,
        position: 0,
        short_description: [],
        tileFacet: [],
        price_information: { price: null, teaser: null },
    };

    static propTypes = {
        name: PropTypes.string.isRequired,
        slug: PropTypes.string,
        id: PropTypes.string,
        image: PropTypes.string,
        facet: PropTypes.string.isRequired,
        tileFacet: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        short_description: PropTypes.array.isRequired,
        price_information: PropTypes.object.isRequired,
        badge: PropTypes.string,
        productErrorId: PropTypes.string,
        activeRegion: PropTypes.object,
        skipTests: PropTypes.bool.isRequired,
        openModal: PropTypes.func.isRequired,
        setProductAmount: PropTypes.func.isRequired,
        cartStatus: PropTypes.string,
        cartErrorId: PropTypes.string,
        cartItems: PropTypes.object.isRequired,
        baseurl: PropTypes.string.isRequired,
        isMobile: PropTypes.bool.isRequired,
        fetchingId: PropTypes.string,
        cooperative: PropTypes.string,
        position: PropTypes.number,
        isCmsEnabled: PropTypes.bool.isRequired,
        has_discount: PropTypes.bool,
        /**
         * This property is only needed for tracking and it reveals the context where the product tile gets used
         */
        list: PropTypes.string.isRequired,
    };

    constructor(props) {
        super(props);

        const hasImage = props.image ? assetsCache.has(this.getProductImageSource(props)) : false;

        const amount = get(props, `cartItems[${ props.id }].amount`, 0);

        this.state = {
            isLoading: false,
            isVisible: hasImage,
            isLoaded: hasImage,
            amount,
        };
    }

    componentDidMount() {
        this.trackProductImpression(this.state);

        if (!this.state.isLoaded) {
            register(this, 'product-tile', {
                threshold: 0.05,
                rootMargin: '200px',
            });
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.isVisible && nextProps.image && !this.state.isLoaded && !this.state.isLoading) {
            this.lazyLoad(nextProps);
        }

        if (nextProps.isMobile !== this.props.isMobile) {
            assetsCache.clear();
        }

        const oldAmount = get(this.props, `cartItems[${ this.props.id }].amount`, 0);
        const newAmount = get(nextProps, `cartItems[${ nextProps.id }].amount`, 0);

        if (oldAmount !== newAmount || this.props.cartErrorId === this.props.id) {
            this.setState({
                amount: newAmount,
            });
        }
    }

    shouldComponentUpdate({ fetchingId, id }, { isLoaded, amount, isVisible, isLoading }) {
        return (
            isLoaded !== this.state.isLoaded || // was it lazy loaded?
            amount !== this.state.amount || // was its counter updated?
            isVisible !== this.state.isVisible ||
            isLoading !== this.state.isLoading ||
            this.props.fetchingId === id || fetchingId === id // has started/finished fetching?
        );
    }

    componentWillUpdate(nextProps, nextState) {
        this.trackProductImpression(nextState);

        if (nextState.isVisible && !this.state.isVisible) {
            if (!this.state.isLoaded && this.props.image && !this.state.isLoading) {
                this.lazyLoad(nextProps);
            }
        }
    }

    componentWillUnmount() {
        this.wasUnmounted = true;
        unregister(this);
    }

    onImageLoaded = src => {
        if (this.wasUnmounted) {
            return;
        }

        assetsCache.set(src, true);

        this.setState({
            isLoaded: true,
        });
    };

    trackProductImpression = (state) => {
        // track a product impression only once
        if (state.isVisible && this.props.image && !this._wasProductImpressionTracked) {
            tracking.productImpression(this.props);
            if (this.props.has_discount) {
                tracking.promotionView(this.props);
            }
            this._wasProductImpressionTracked = true;
        }
    };

    _wasProductImpressionTracked = false;


    getProductImageSource(props) {
        return imageByDpi(productImage(props.image, props.isMobile ? THUMB : TILE));
    }

    setAmount = amount => {
        amount = Number(amount);

        // Abort if there is currently a server request
        if (this.state.amount === amount) {
            return;
        }

        if (
            this.props.skipTests ||
            this.props.activeRegion &&
            this.props.activeRegion.cooperative === this.props.cooperative &&
            this.props.cartStatus === OPEN
        ) {
            this.setState({
                amount,
            });
        }

        this.sendAmount(amount);
    };

    add = () => {
        this.setAmount(this.state.amount + 1);
    };

    lazyLoad = props => {
        const src = this.getProductImageSource(props);
        this.setState({ isLoading: true });
        loadImage(src).then(() => this.onImageLoaded(src));
    };

    openDetailOverlay = e => {
        const href = e.currentTarget.getAttribute('href');
        e.preventDefault();
        stopPropagation(e);
        tracking.productClick(this.props);

        if (this.props.has_discount) {
            tracking.promotionClick(this.props, {
                creative: this.props.list,
            });
        }

        this.props.openModal({
            layout: 'product-detail',
            isAsync: true,
            href: href,
        });
    };

    remove = () => {
        if (this.state.amount) {
            this.setAmount(this.state.amount - 1);
        }
    };

    sendAmount = debounce(function(amount) {
        const { setProductAmount } = this.props;

        setProductAmount({ product: this.props, amount });
    }, COUNTER_CLICK_DEBOUNCE);

    wasUnmounted = false;

    renderProductImage() {
        const { image, id, fetchingId, price_information } = this.props;
        const { isLoaded, isVisible } = this.state;
        const { badge } = price_information;
        const iconSymbol = (this.activeCategory && categoryIcons[this.activeCategory.id]) || categoryIcons.default;
        const isSubmittingValues = fetchingId && fetchingId === id;

        return (
            <div key="image" className="product-tile--image">
                <div className="product-tile--image-inner">
                    <div
                        className={ classnames('product-tile--image-content', {
                            'product-tile--image-inner__loaded': isLoaded,
                        }) }
                        style={ {
                            backgroundImage:
                                image && isLoaded ? `url('${ this.getProductImageSource(this.props) }')` : 'none',
                        } }
                    >
                        { !isLoaded && isVisible && iconSymbol &&
                        <Loader facet={ 'icon' } size={ 'large' } symbol={ iconSymbol }/> }
                        { isSubmittingValues && <div className="product-tile--loader-animator">
                            <div key="loader" className="product-tile--loader">
                                <Loader size="small"/>
                            </div>
                        </div> }
                    </div>
                </div>
                { badge && isLoaded &&
                <img src={ imageByDpi(productImage(badge, BADGE_TILE)) } className="product-tile--badge"/> }
            </div>
        );
    }

    renderOverlayLink() {
        const { slug, baseurl, isCmsEnabled } = this.props;

        // ATTENTION: another dirty hack here to solve https://jira.migros.net/browse/MCART-1481
        // this is dumb hack for a dumb issue due to the messy javascript events generated by django CMS when a user is logged in
        return (
            createElement(isCmsEnabled ? 'button' : 'a', {
                className: 'product-tile--button product-tile--more-button u-reset',
                'aria-label': t('accessibility.label.product_detail'),
                onClick: this.openDetailOverlay,
                href: `${ baseurl }/${ normaliseTrailingSlash(slug) }`,
            }, <Icon symbol="product-detail" size="large"/>)
        );
    }

    renderTileContent() {
        const { name, facet, price_information, id, short_description } = this.props;
        const { isVisible, isLoaded, amount } = this.state;

        const { add, remove } = this;
        const methods = { add, remove };

        return [
            isVisible && id && <div key="counter" className="product-tile--counter">
                <CartCounter productName={ name }
                    amount={ amount }
                    facet={ `tile-${ facet }` }
                    { ...methods }
                    id={ id }/>
            </div>,
            this.renderProductImage(),
            <ProductMeta
                key="meta"
                name={ name }
                short_description={ short_description }
                price_information={ price_information }
            >
            </ProductMeta>,
            <div key="more" className="product-tile--more">
                { isLoaded && this.renderOverlayLink() }
            </div>,
        ];
    }

    render() {
        const { facet, tileFacet } = this.props;
        const { isVisible } = this.state;

        return (
            <article
                ref="root"
                className={ classnames('product-tile', mapFacets(facet, 'product-tile'), mapFacets(tileFacet, 'product-tile')) }
                itemScope
                itemType="http://schema.org/Product"
            >
                { isVisible && this.renderTileContent() }
                { !isVisible && <div>&nbsp;</div> }
            </article>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        activeRegion: 'region.active',
        fetchingId: 'cart.fetchingId',
        cartErrorId: 'cart.productError.fetchingId',
        cartStatus: 'cart.state',
        cartItems: 'cart.items',
        baseurl: 'router.baseurl',
        isCmsEnabled: 'cms.isEnabled',
        isMobile: 'viewport.isMobile',
    }),
    reduxUtils.mapDispatchToProps({
        openModal: 'modal.open',
        setProductAmount: 'cart.setProductAmount',
    }),
)(ProductTile);

/* eslint-enable camelcase */



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/product-tile/index.js